import $ from 'jquery';
import ContentSyncSlider from './content-sync-slider';
import LottieAnimationLoader from './lottie-animation-loader';
export default class TabbedFeature {
    static selector = '.js__tabbed-feature';
    static init(onSlideActivate) {
        $(this.selector).each((_index, root) => {
            this.initialize({
                $root: $(root),
                loopPlayers: new Map(),
                onSlideActivate,
                pendingLoopPlayers: new Map(),
                reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
                slider: null,
            });
        });
    }
    static initialize(state) {
        state.$root.find('.js__tabbed-feature-tab').on('click', event => {
            this.activateTab(state, $(event.currentTarget));
        });
        state.$root.find('.js__tabbed-feature-tablist').on('keydown', event => this.handleTabKeydown(state, event));
        state.$root.find('.js__tabbed-feature-animation-button').on('click', event => {
            $(event.currentTarget)
                .find('.js__tabbed-feature-loop-animation')
                .toArray()
                .forEach(container => void this.playLoopAnimation(state, container));
        });
        state.$root.find('.js__tabbed-feature-info-button').on('click', () => this.toggleInfoPopover(state));
        $(window).on('resize.tabbedFeature', () => {
            if (state.$root.find('.js__tabbed-feature-info-button').attr('aria-expanded') === 'true') {
                this.positionInfoTooltip(state);
            }
        });
        $(document).on('click.tabbedFeature', event => {
            if (!$(event.target).closest('.js__tabbed-feature-info').length) {
                this.closeInfoPopover(state, false);
            }
        });
        state.$root.on('keydown', event => {
            if (event.key === 'Escape') {
                this.closeInfoPopover(state, true);
            }
        });
        const $initialTab = state.$root.find('.js__tabbed-feature-tab[aria-selected="true"]').first();
        if ($initialTab.length) {
            this.activateTab(state, $initialTab);
        }
        else {
            this.syncLoopAnimations(state);
        }
    }
    static activateTab(state, $activeTab) {
        const tab = $activeTab.attr('data-tabbed-feature-tab');
        const layout = $activeTab.attr('data-tabbed-feature-layout');
        const type = $activeTab.attr('data-tabbed-feature-type');
        if (!tab || !type) {
            return;
        }
        state.$root.attr('data-active-tab', tab);
        state.$root.attr('data-ga-view-event-label', $activeTab.attr('data-ga-slider-view-event-label') ?? '');
        if (layout) {
            state.$root.attr('data-active-layout', layout);
        }
        state.$root.find('.js__tabbed-feature-tab').each((_index, element) => {
            const $tab = $(element);
            const isActive = $tab.attr('data-tabbed-feature-tab') === tab;
            $tab.toggleClass('active bg-accent', isActive);
            $tab.attr('aria-selected', String(isActive));
            $tab.attr('tabindex', isActive ? '0' : '-1');
        });
        state.$root.find('.js__tabbed-feature-content').each((_index, element) => {
            const $pane = $(element);
            const isActive = $pane.attr('data-tabbed-feature-pane') === tab;
            $pane.toggleClass('d-flex opacity-100 visible', isActive);
            $pane.toggleClass('d-none opacity-0 invisible', !isActive);
            $pane.attr('aria-hidden', String(!isActive));
        });
        state.$root.find('.js__tabbed-feature-media').each((_index, element) => {
            const $pane = $(element);
            const isActive = $pane.attr('data-tabbed-feature-pane') === tab;
            $pane.prop('hidden', !isActive);
            $pane.attr('aria-hidden', String(!isActive));
        });
        this.closeInfoPopover(state, false);
        if (type !== 'slider') {
            state.slider?.deactivate();
        }
        if (type === 'slider') {
            this.initializeSlider(state);
            state.slider?.activate();
        }
        this.syncLoopAnimations(state);
    }
    static handleTabKeydown(state, event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            return;
        }
        event.preventDefault();
        const $tabs = state.$root.find('.js__tabbed-feature-tab');
        const currentIndex = $tabs.index(event.target);
        let nextIndex = currentIndex;
        if (event.key === 'Home') {
            nextIndex = 0;
        }
        else if (event.key === 'End') {
            nextIndex = $tabs.length - 1;
        }
        else {
            const offset = event.key === 'ArrowRight' ? 1 : -1;
            nextIndex = (currentIndex + offset + $tabs.length) % $tabs.length;
        }
        const $nextTab = $tabs.eq(nextIndex);
        this.activateTab(state, $nextTab);
        $nextTab.trigger('focus');
    }
    static initializeSlider(state) {
        if (state.slider) {
            return;
        }
        const $sliderRoot = state.$root.find('.js__tabbed-feature-pane[data-animation-source]').first();
        if (!$sliderRoot.length) {
            return;
        }
        state.slider = ContentSyncSlider.create(state.$root, {
            content: '.js__tabbed-feature-slider-content',
            nextButton: '.js__tabbed-feature-slider-next',
            pagination: '.js__tabbed-feature-slider-pagination',
            previousButton: '.js__tabbed-feature-slider-previous',
            slider: '.js__tabbed-feature-slider',
        }, $sliderRoot, state.onSlideActivate);
    }
    static syncLoopAnimations(state) {
        const activeContainers = new Set(state.$root.find('.js__tabbed-feature-pane:not([hidden]) .js__tabbed-feature-loop-animation').toArray());
        state.loopPlayers.forEach((player, container) => {
            if (!activeContainers.has(container)) {
                player.pause();
            }
        });
        activeContainers.forEach(container => {
            void this.playLoopAnimation(state, container);
        });
    }
    static playLoopAnimation(state, container) {
        if (!container) {
            return Promise.resolve(null);
        }
        const existingPlayer = state.loopPlayers.get(container);
        if (existingPlayer) {
            this.startAnimation(state, existingPlayer, this.getLoopAnimationStartFrame(container));
            return Promise.resolve(existingPlayer);
        }
        const pendingPlayer = state.pendingLoopPlayers.get(container);
        if (pendingPlayer) {
            return pendingPlayer;
        }
        const $container = $(container);
        const source = $container.attr('data-animation-source');
        const assets = LottieAnimationLoader.parseAssetSources($container.attr('data-animation-assets'));
        if (!source || !assets) {
            return Promise.resolve(null);
        }
        const playerRequest = LottieAnimationLoader.init(source, assets)
            .load(container)
            .then(player => {
            state.loopPlayers.set(container, player);
            $container.addClass('is-loaded');
            if (this.isLoopAnimationActive(container)) {
                this.startAnimation(state, player, this.getLoopAnimationStartFrame(container));
            }
            else {
                player.pause();
            }
            return player;
        })
            .catch(() => null)
            .finally(() => state.pendingLoopPlayers.delete(container));
        state.pendingLoopPlayers.set(container, playerRequest);
        return playerRequest;
    }
    static isLoopAnimationActive(container) {
        return !$(container).closest('.js__tabbed-feature-pane').prop('hidden');
    }
    static getLoopAnimationStartFrame(container) {
        const startFrame = Number($(container).attr('data-animation-start-frame') ?? 0);
        return Number.isFinite(startFrame) && startFrame >= 0 ? startFrame : 0;
    }
    static startAnimation(state, player, startFrame) {
        if (state.reducedMotion) {
            player.goToAndStop(Math.max(player.totalFrames - 1, 0), true);
            return;
        }
        player.setLoop(true);
        player.goToAndPlay(startFrame, true);
    }
    static toggleInfoPopover(state) {
        const isOpen = state.$root.find('.js__tabbed-feature-info-button').attr('aria-expanded') === 'true';
        if (isOpen) {
            this.closeInfoPopover(state, false);
            return;
        }
        state.$root.find('.js__tabbed-feature-info-button').attr('aria-expanded', 'true').addClass('active');
        state.$root.find('.js__tabbed-feature-info-popover').prop('hidden', false);
        state.$root.find('.js__tabbed-feature-media-info').prop('hidden', false).attr('aria-hidden', 'false');
        this.positionInfoTooltip(state);
    }
    static positionInfoTooltip(state) {
        const tooltip = state.$root.find('.js__tabbed-feature-info-popover:not([hidden])').get(0);
        const boundary = state.$root.find('.tabbed-feature-content-panel').get(0);
        if (!tooltip || !boundary) {
            return;
        }
        tooltip.style.setProperty('--tabbed-feature-info-tooltip-offset-x', '0px');
        const tooltipRect = tooltip.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const safeLeft = Math.max(boundaryRect.left, 0) + 8;
        const safeRight = Math.min(boundaryRect.right, window.innerWidth) - 8;
        let offset = 0;
        if (tooltipRect.left < safeLeft) {
            offset = safeLeft - tooltipRect.left;
        }
        else if (tooltipRect.right > safeRight) {
            offset = safeRight - tooltipRect.right;
        }
        tooltip.style.setProperty('--tabbed-feature-info-tooltip-offset-x', `${offset}px`);
    }
    static closeInfoPopover(state, returnFocus) {
        const $button = state.$root.find('.js__tabbed-feature-info-button');
        const wasOpen = $button.attr('aria-expanded') === 'true';
        $button.attr('aria-expanded', 'false').removeClass('active');
        state.$root.find('.js__tabbed-feature-info-popover').prop('hidden', true);
        state.$root.find('.js__tabbed-feature-media-info').prop('hidden', true).attr('aria-hidden', 'true');
        if (returnFocus && wasOpen) {
            $button.trigger('focus');
        }
    }
}
